export type OpenReplayLiveViewMessage = readonly [number, ...unknown[]]

// Message type ids from @openreplay/tracker 9.0.12 lib/common/messages.gen.d.ts, re-check them when the tracker is upgraded
export const OpenReplayLiveViewMessageType = {
    Timestamp: 0,
    SetPageLocation: 4,
    SetViewportSize: 5,
    SetViewportScroll: 6,
    CreateDocument: 7,
    CreateElementNode: 8,
    CreateTextNode: 9,
    MoveNode: 10,
    RemoveNode: 11,
    SetNodeAttribute: 12,
    RemoveNodeAttribute: 13,
    SetNodeData: 14,
    SetNodeScroll: 16,
    SetInputTarget: 17,
    SetInputValue: 18,
    SetInputChecked: 19,
    MouseMove: 20,
    CSSInsertRule: 37,
    CSSDeleteRule: 38,
    StringDict: 50,
    SetNodeAttributeDict: 51,
    SetPageVisibility: 55,
    LoadFontFace: 57,
    SetNodeFocus: 58,
    SetNodeAttributeURLBased: 60,
    SetCSSDataURLBased: 61,
    CSSInsertRuleURLBased: 67,
    MouseClick: 69,
    CreateIFrameDocument: 70,
    AdoptedSSReplaceURLBased: 71,
    AdoptedSSInsertRuleURLBased: 73,
    AdoptedSSDeleteRule: 75,
    AdoptedSSAddOwner: 76,
    AdoptedSSRemoveOwner: 77,
    SelectionChange: 113,
    TabChange: 117,
    TabData: 118,
} as const

const PAGE_DRAWING_MESSAGE_TYPES = new Set<number>(Object.values(OpenReplayLiveViewMessageType))

// Return links put their token in the path (e.g. /a/:returnToken), so long letter-and-digit segments are hidden too
const TOKEN_LIKE_PATH_SEGMENT_PATTERN = /^(?=.*\d)(?=.*[a-z])[\w\-.~%]{12,}$/i
const REDACTED_PATH_SEGMENT = ':redacted'
const ABSOLUTE_URL_QUERY_AND_HASH_PATTERN = /((?:https?:)?\/\/[^\s?#,'"()]+)[?#][^\s,'"()]*/gi
// at least one character before the ? or #, so fragment references such as url(#gradient) stay intact
const CSS_URL_QUERY_AND_HASH_PATTERN = /url\((['"]?)(?!data:)([^'"()?#]+)[?#][^'"()]*\1\)/gi

const HTTP_PROTOCOLS = new Set(['http:', 'https:'])

const tryParseUrl = (url: string): URL | null => {
    try {
        return new URL(url)
    } catch {
        return null
    }
}

// data: and blob: urls have no query to strip, fragment references (an SVG <use href="#icon">) are kept, and relative urls are cut at the first ? or #
const stripQueryAndHash = (url: unknown): string => {
    if (typeof url !== 'string') {
        return ''
    }
    if (url.startsWith('#')) {
        return url
    }
    const parsedUrl = tryParseUrl(url)
    if (!parsedUrl) {
        return url.split(/[?#]/)[0]
    }
    return HTTP_PROTOCOLS.has(parsedUrl.protocol) ? `${parsedUrl.origin}${parsedUrl.pathname}` : url
}

const redactTokenLikePathSegments = (pathname: string): string =>
    pathname
        .split('/')
        .map((segment) => (TOKEN_LIKE_PATH_SEGMENT_PATTERN.test(segment) ? REDACTED_PATH_SEGMENT : segment))
        .join('/')

const sanitizePageUrl = (url: unknown): string => {
    const urlWithoutQuery = stripQueryAndHash(url)
    const parsedUrl = tryParseUrl(urlWithoutQuery)
    if (!parsedUrl || !HTTP_PROTOCOLS.has(parsedUrl.protocol)) {
        return urlWithoutQuery
    }
    return `${parsedUrl.origin}${redactTokenLikePathSegments(parsedUrl.pathname)}`
}

const stripQueryAndHashFromCssUrls = (css: unknown): unknown => (typeof css === 'string' ? css.replace(CSS_URL_QUERY_AND_HASH_PATTERN, 'url($1$2$1)') : css)

// srcset and other attribute values arrive as dictionary strings, so any signed url in them loses its query
const stripQueryAndHashFromAbsoluteUrls = (text: unknown): unknown => (typeof text === 'string' ? text.replace(ABSOLUTE_URL_QUERY_AND_HASH_PATTERN, '$1') : text)

const removeLiveViewSensitiveFields = (message: OpenReplayLiveViewMessage): OpenReplayLiveViewMessage => {
    const [type, ...fields] = message
    switch (type) {
        case OpenReplayLiveViewMessageType.SetPageLocation: {
            const [url, referrer, ...rest] = fields
            return [type, sanitizePageUrl(url), sanitizePageUrl(referrer), ...rest]
        }
        case OpenReplayLiveViewMessageType.SetNodeAttributeURLBased: {
            const [id, name, value, baseUrl] = fields
            const sanitizedValue = name === 'style' ? stripQueryAndHashFromCssUrls(value) : stripQueryAndHash(value)
            return [type, id, name, sanitizedValue, sanitizePageUrl(baseUrl)]
        }
        case OpenReplayLiveViewMessageType.SetCSSDataURLBased:
        case OpenReplayLiveViewMessageType.AdoptedSSReplaceURLBased: {
            const [id, css, baseUrl] = fields
            return [type, id, stripQueryAndHashFromCssUrls(css), sanitizePageUrl(baseUrl)]
        }
        case OpenReplayLiveViewMessageType.CSSInsertRuleURLBased:
        case OpenReplayLiveViewMessageType.AdoptedSSInsertRuleURLBased: {
            const [id, rule, index, baseUrl] = fields
            return [type, id, stripQueryAndHashFromCssUrls(rule), index, sanitizePageUrl(baseUrl)]
        }
        case OpenReplayLiveViewMessageType.LoadFontFace: {
            const [parentId, family, source, ...rest] = fields
            return [type, parentId, family, stripQueryAndHashFromCssUrls(source), ...rest]
        }
        case OpenReplayLiveViewMessageType.StringDict: {
            const [key, value] = fields
            return [type, key, stripQueryAndHashFromAbsoluteUrls(value)]
        }
        case OpenReplayLiveViewMessageType.SelectionChange: {
            const [selectionStartNodeId, selectionEndNodeId] = fields
            return [type, selectionStartNodeId, selectionEndNodeId, '']
        }
        default:
            return message
    }
}

export const filterOpenReplayMessagesForLiveView = (messages: readonly OpenReplayLiveViewMessage[]): OpenReplayLiveViewMessage[] =>
    messages.filter(([type]) => PAGE_DRAWING_MESSAGE_TYPES.has(type)).map(removeLiveViewSensitiveFields)
